import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, LogOut, ArrowLeft, Edit2, Trash2, Sparkles, Save, Star, Share2, Download, Globe, Lock, Settings, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import LanguageSelector from "@/components/LanguageSelector";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DreamDetail({ user, onLogout }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [dream, setDream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [humanAnalysis, setHumanAnalysis] = useState("");
  const [editingHumanAnalysis, setEditingHumanAnalysis] = useState(false);
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  // Map i18n language to analysis language
  const langMap = { fi: "finnish", en: "english", fr: "french", de: "german", es: "spanish" };
  const [selectedLanguage, setSelectedLanguage] = useState(langMap[i18n.language] || "english");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [generatingArtwork, setGeneratingArtwork] = useState(false);
  const [hasArtworkTrial, setHasArtworkTrial] = useState(true);
  const [streakCredits, setStreakCredits] = useState(0);
  const [streak7Achieved, setStreak7Achieved] = useState(false);
  const [artStyles, setArtStyles] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState("surrealist");

  useEffect(() => {
    if (id) {
      fetchDream();
      fetchArtworkStatus();
      fetchStreakStatus();
      fetchArtStyles();
    } else {
      navigate('/dreams');
    }
  }, [id]);

  const fetchArtStyles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/artwork-styles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const loaded = res.data?.styles || [];
      setArtStyles(loaded);
      // Honor ?style= query param if present and unlocked
      const requested = searchParams.get("style");
      if (requested) {
        const found = loaded.find((s) => s.key === requested && s.unlocked);
        if (found) setSelectedStyle(requested);
      }
    } catch (e) {
      // silently ignore
    }
  };

  const fetchStreakStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/user/streak`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const current = res.data?.current_streak || 0;
      const longest = res.data?.longest_streak || 0;
      // Milestone reached if either current or all-time best ever hit 7+
      setStreak7Achieved(current >= 7 || longest >= 7);
    } catch (e) {
      // silently ignore — milestone banner is optional
    }
  };

  const fetchArtworkStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/artwork-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Allow generation if free trial OR premium OR streak reward credits available
      setHasArtworkTrial(
        response.data.has_free_trial ||
        response.data.is_premium ||
        (response.data.streak_credits || 0) > 0
      );
      setStreakCredits(response.data.streak_credits || 0);
    } catch (error) {
      console.error("Failed to fetch artwork status");
    }
  };

  const fetchDream = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dreams/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDream(response.data);
      setHumanAnalysis(response.data.human_analysis || "");
      setRating(response.data.ai_analysis_rating || 0);
    } catch (error) {
      toast.error("Failed to load dream");
      navigate('/dreams');
    } finally {
      setLoading(false);
    }
  };

  const togglePublic = async () => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = !dream.is_public;
      await axios.patch(`${API}/dreams/${id}`, 
        { is_public: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDream({ ...dream, is_public: newStatus });
      toast.success(newStatus 
        ? (t('dreamDetail.sharedToCommunity') || 'Uni jaettu yhteisöön!') 
        : (t('dreamDetail.removedFromCommunity') || 'Uni poistettu yhteisöstä')
      );
    } catch (error) {
      toast.error(t('dreamDetail.shareFailed') || 'Jakaminen epäonnistui');
    }
  };

  const handleAIAnalysis = async () => {
    setAnalyzing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(t('common.loginRequired') || "Please log in again");
        return;
      }
      const response = await axios.post(
        `${API}/dreams/${id}/analyze`,
        { language: selectedLanguage },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      setDream({ ...dream, ai_analysis: response.data.analysis, ai_analysis_language: response.data.language });
      
      if (response.data.remaining_free !== null && response.data.remaining_free !== undefined) {
        toast.success(
          response.data.cached 
            ? (t('dreamDetail.analysisLoaded') || "Analysis loaded")
            : `${t('dreamDetail.analysisComplete') || "Analysis complete!"} ${response.data.remaining_free} ${t('dreamDetail.freeRemaining') || "free analyses remaining"}`
        );
      } else {
        toast.success(response.data.cached ? (t('dreamDetail.analysisLoaded') || "Analysis loaded") : (t('dreamDetail.analysisComplete') || "Analysis complete!"));
      }
    } catch (error) {
      console.error("Analysis error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error(t('dreamDetail.analysisTimeout') || "Analysis timed out. Please try again.");
      } else if (error.response?.status === 403) {
        const message = error.response?.data?.detail || (t('dreamDetail.premiumRequired') || "Premium subscription required for AI analysis");
        toast.error(message);
        setTimeout(() => navigate('/premium'), 1500);
      } else if (error.response?.status === 401) {
        toast.error(t('common.loginRequired') || "Session expired. Please log in again.");
      } else {
        toast.error(error.response?.data?.detail || (t('dreamDetail.analysisFailed') || "Analysis failed. Please try again."));
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRating = async (newRating) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/dreams/${id}/rate`,
        { rating: newRating },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRating(newRating);
      toast.success("Rating saved!");
    } catch (error) {
      toast.error("Failed to save rating");
    }
  };

  const generateCardImage = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx.roundRect) {
      ctx.roundRect = function(x, y, width, height, radius) {
        if (typeof radius === 'number') {
          radius = { tl: radius, tr: radius, br: radius, bl: radius };
        }
        this.beginPath();
        this.moveTo(x + radius.tl, y);
        this.lineTo(x + width - radius.tr, y);
        this.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
        this.lineTo(x + width, y + height - radius.br);
        this.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
        this.lineTo(x + radius.bl, y + height);
        this.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
        this.lineTo(x, y + radius.tl);
        this.quadraticCurveTo(x, y, x + radius.tl, y);
        this.closePath();
      };
    }
    
    canvas.width = 1080;
    canvas.height = 1080;
    
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#E6E6FA');
    gradient.addColorStop(0.5, '#D8BFD8');
    gradient.addColorStop(1, '#98FF98');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    for (let i = 0; i < 80; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const radius = Math.random() * 2 + 1;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    
    const cardPadding = 80;
    const cardX = cardPadding;
    const cardY = cardPadding + 100;
    const cardWidth = canvas.width - (cardPadding * 2);
    const cardHeight = canvas.height - (cardPadding * 2) - 200;
    const cardRadius = 40;
    
    ctx.shadowColor = 'rgba(26, 26, 46, 0.15)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 15;
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.beginPath();
    ctx.roundRect(cardX, cardY, cardWidth, cardHeight, cardRadius);
    ctx.fill();
    
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    
    ctx.fillStyle = '#1A1A2E';
    ctx.beginPath();
    ctx.arc(canvas.width / 2, 120, 50, 0, Math.PI * 2);
    ctx.fill();
    
    const moonStars = [
      { x: canvas.width / 2 - 100, y: 100 },
      { x: canvas.width / 2 + 100, y: 100 },
      { x: canvas.width / 2 - 70, y: 150 }
    ];
    moonStars.forEach(star => {
      ctx.fillStyle = '#98FF98';
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
        const x = star.x + Math.cos(angle) * 8;
        const y = star.y + Math.sin(angle) * 8;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
    });
    
    const dateText = formatDate(dream.date);
    ctx.font = '24px sans-serif';
    ctx.fillStyle = 'rgba(26, 26, 46, 0.4)';
    ctx.textAlign = 'right';
    ctx.fillText(dateText, cardX + cardWidth - 40, cardY + 60);
    
    ctx.font = 'italic 600 56px serif';
    ctx.fillStyle = '#1A1A2E';
    ctx.textAlign = 'center';
    
    const titleText = dream.title.length > 35 ? dream.title.substring(0, 35) + '...' : dream.title;
    ctx.fillText(titleText, canvas.width / 2, cardY + 140);
    
    ctx.strokeStyle = '#98FF98';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(canvas.width / 2 - 150, cardY + 170);
    ctx.lineTo(canvas.width / 2 + 150, cardY + 170);
    ctx.stroke();
    
    ctx.font = '28px sans-serif';
    ctx.fillStyle = 'rgba(26, 26, 46, 0.8)';
    ctx.textAlign = 'left';
    
    const contentText = dream.content.length > 500 ? dream.content.substring(0, 500) + '...' : dream.content;
    
    const maxWidth = cardWidth - 100;
    const lineHeight = 40;
    const startY = cardY + 230;
    const lines = [];
    let currentLine = '';
    
    contentText.split(' ').forEach(word => {
      const testLine = currentLine + word + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine !== '') {
        lines.push(currentLine);
        currentLine = word + ' ';
      } else {
        currentLine = testLine;
      }
    });
    lines.push(currentLine);
    
    lines.slice(0, 12).forEach((line, i) => {
      ctx.fillText(line.trim(), cardX + 50, startY + (i * lineHeight));
    });
    
    const bottomY = cardY + cardHeight - 70;
    
    ctx.font = 'italic 400 48px serif';
    ctx.fillStyle = '#1A1A2E';
    ctx.textAlign = 'center';
    ctx.fillText('Uneksia', canvas.width / 2, bottomY);
    
    ctx.font = '24px sans-serif';
    ctx.fillStyle = 'rgba(26, 26, 46, 0.5)';
    ctx.fillText('Explore your dreams', canvas.width / 2, bottomY + 35);
    
    return canvas;
  };

  const handleDownloadCard = () => {
    try {
      const canvas = generateCardImage();
      const link = document.createElement('a');
      link.download = `unipaivakija-${dream.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Card downloaded!");
    } catch (error) {
      console.error("Card generation error:", error);
      toast.error("Failed to generate card. Please try again.");
    }
  };

  const handleShareCard = async () => {
    try {
      const canvas = generateCardImage();
      
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error("Failed to generate card image");
          return;
        }
        
        const file = new File([blob], 'dream-card.png', { type: 'image/png' });
        
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: dream.title,
              text: `Check out my dream: ${dream.title}`
            });
            toast.success("Shared successfully!");
          } catch (error) {
            if (error.name !== 'AbortError') {
              handleDownloadCard();
            }
          }
        } else {
          handleDownloadCard();
        }
      }, 'image/png');
    } catch (error) {
      console.error("Share card error:", error);
      toast.error("Failed to share card. Please try again.");
    }
  };

  const handleGenerateArtwork = async () => {
    if (!hasArtworkTrial && !user?.is_premium) {
      toast.error("Ilmainen kokeilu käytetty / Free trial used");
      return;
    }

    setGeneratingArtwork(true);
    toast.info(t('dreamDetail.generatingArtwork') || "Luodaan unitaidetta... 20-30 sekuntia");

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/dreams/${id}/generate-artwork`,
        { style: selectedStyle },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.image) {
        if (response.data.free_trial_used) {
          setHasArtworkTrial(false);
        }

        // Display the artwork on the page (saved in dream object)
        setDream({
          ...dream,
          artwork: response.data.image,
          artwork_mime: response.data.mime_type,
        });

        // Refresh artwork status (streak credit may have decremented)
        fetchArtworkStatus();

        toast.success(t('dreamDetail.artworkSuccess') || "🎨 Unitaide luotu!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate artwork");
    } finally {
      setGeneratingArtwork(false);
    }
  };

  const handleDownloadArtwork = () => {
    if (!dream?.artwork) return;
    const mimeType = dream.artwork_mime || 'image/png';
    const extension = mimeType.includes('jpeg') || mimeType.includes('jpg') ? 'jpg' : 'png';

    const byteCharacters = atob(dream.artwork);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `unipaivakija-artwork-${dream.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.${extension}`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveHumanAnalysis = async () => {
    setSavingAnalysis(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/dreams/${id}`,
        { human_analysis: humanAnalysis },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDream({ ...dream, human_analysis: humanAnalysis });
      setEditingHumanAnalysis(false);
      toast.success("Your analysis saved!");
    } catch (error) {
      toast.error("Failed to save analysis");
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dreams/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dream deleted");
      navigate('/dreams');
    } catch (error) {
      toast.error("Failed to delete dream");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-dream">
        <p className="text-void/60 font-body">Loading dream...</p>
      </div>
    );
  }

  if (!dream) return null;

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
                <img src="/logo-new.png" alt="Uneksia" className="w-12 h-12" />
                <span className="text-2xl font-heading font-light text-void tracking-tight">{t('appName')}</span>
              </div>
              <div className="hidden md:flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dashboard"
                >
                  {t('nav.dashboard')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dreams')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dreams"
                >
                  {t('nav.myDreams')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/community-hub')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-community-hub"
                >
                  {t('nav.community')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/blog")}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-blog"
                >
                  {t("blog.title")}
                </Button>
                {user?.is_admin && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/admin')}
                    className="font-body text-red-500 hover:text-red-600 font-medium"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Button>
                )}
                {!user?.is_premium && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/premium')}
                    className="font-body text-lucid hover:text-lucid/80 font-medium"
                    data-testid="nav-premium"
                  >
                    ✨ {t('nav.premium')}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector variant="minimal" />
              <Button
                variant="ghost"
                onClick={() => navigate('/settings')}
                className="text-void hover:text-void/80"
                data-testid="settings-button"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-void hover:text-void/80"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-32 md:pb-12 overflow-x-hidden">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dreams')}
          className="mb-6 text-void hover:text-void/80 font-body"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back') || 'Back to Dreams'}
        </Button>

        {/* Dream Content */}
        <div className="glass-effect rounded-3xl p-8 mb-8">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-heading font-light text-void tracking-tight mb-2" data-testid="dream-title">
                {dream.title}
              </h1>
              <p className="text-void/60 font-body" data-testid="dream-date">{formatDate(dream.date)}</p>
              {dream.is_public && (
                <span className="inline-flex items-center gap-1 mt-1 text-xs font-body text-green-600 bg-green-50 px-2 py-0.5 rounded-full" data-testid="public-badge">
                  <Globe className="w-3 h-3" />
                  {t('dreamDetail.publicDream') || 'Jaettu yhteisöön'}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/journal/edit/${dream.id}`)}
                className="text-void hover:text-void/80"
                title={t('common.edit') || 'Muokkaa'}
                data-testid="edit-dream-button"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={togglePublic}
                className={dream.is_public ? "text-green-600 hover:text-green-700" : "text-purple-400 hover:text-purple-600"}
                title={dream.is_public ? (t('dreamDetail.removeFromCommunity') || 'Poista yhteisöstä') : (t('dreamDetail.shareToCommunity') || 'Jaa yhteisöön')}
                data-testid="toggle-public-button"
              >
                {dream.is_public ? <Globe className="w-5 h-5" /> : <Globe className="w-5 h-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowShareModal(true)}
                className="text-void hover:text-void/80"
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-void hover:text-destructive"
                    data-testid="delete-button"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white rounded-3xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading text-void">{t('dreamDetail.deleteTitle') || 'Delete Dream?'}</AlertDialogTitle>
                    <AlertDialogDescription className="font-body">
                      {t('dreamDetail.deleteDescription') || 'This action cannot be undone. This will permanently delete your dream and its analysis.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full font-body">{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      className="bg-destructive text-white hover:bg-destructive/90 rounded-full font-body"
                      data-testid="confirm-delete-button"
                    >
                      {t('common.delete') || 'Delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* Tags */}
          {dream.tags && dream.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="dream-tags">
              {dream.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="text-sm px-3 py-1 bg-lucid/20 text-void rounded-full font-body"
                >
                  {t(`tags.${tag}`) || tag}
                </span>
              ))}
            </div>
          )}

          {/* Dream Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-void/80 font-body leading-relaxed whitespace-pre-wrap" data-testid="dream-content">
              {dream.content}
            </p>
          </div>
        </div>

        {/* Streak reward callout — only for FREE users who have earned a streak credit.
            Premium users don't need this banner since they get unlimited AI art. */}
        {streak7Achieved && !dream.artwork && streakCredits > 0 && !user?.is_premium && (
          <div
            className="relative overflow-hidden glass-effect rounded-3xl p-6 mb-8 bg-gradient-to-br from-amber-50 via-rose-50 to-purple-50 border-2 border-amber-300/50"
            data-testid="streak-reward-callout"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-rose-400 flex items-center justify-center shadow-md flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-light text-void mb-0.5">
                    🎁 {t('dreamDetail.streakRewardTitle') || 'Lunasta palkintosi!'}
                  </h3>
                  <p className="text-sm text-void/70 font-body">
                    {t('dreamDetail.streakRewardSubtitle') || 'Ilmainen AI-unitaide odottaa'}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleGenerateArtwork}
                disabled={generatingArtwork}
                className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white rounded-full font-body font-medium px-6 h-11 shadow-md"
                data-testid="claim-artwork-button"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {generatingArtwork
                  ? (t('dreamDetail.generating') || 'Luodaan...')
                  : (t('dreamDetail.generateArtwork') || 'Luo unitaide')}
              </Button>
            </div>
            {artStyles.length > 0 && (
              <div className="mt-5" data-testid="art-style-picker">
                <p className="text-xs text-void/50 font-body uppercase tracking-wide mb-2">
                  {t('dreamDetail.artStyle') || 'Taidetyyli'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {artStyles.map((s) => (
                    <button
                      key={s.key}
                      disabled={!s.unlocked}
                      onClick={() => s.unlocked && setSelectedStyle(s.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-body transition-all ${
                        selectedStyle === s.key
                          ? 'bg-void text-white'
                          : s.unlocked
                            ? 'bg-white/60 text-void hover:bg-white/80'
                            : 'bg-void/5 text-void/30 cursor-not-allowed'
                      }`}
                      data-testid={`art-style-${s.key}`}
                    >
                      {s.unlocked ? s.label : `🔒 ${s.label}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI Artwork Display Section */}
        {dream.artwork ? (
          <div className="glass-effect rounded-3xl p-6 mb-8" data-testid="ai-artwork-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="text-2xl font-heading font-light text-void flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-purple-500" />
                {t('dreamDetail.aiArtwork') || 'AI-Unitaide'}
              </h2>
              <Button
                onClick={handleDownloadArtwork}
                variant="outline"
                className="rounded-full border-void/20 hover:bg-white/80"
                data-testid="download-artwork-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('common.download') || 'Lataa'}
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden bg-void/5 flex items-center justify-center">
              <img
                src={`data:${dream.artwork_mime || 'image/png'};base64,${dream.artwork}`}
                alt={`AI artwork for ${dream.title}`}
                className="w-full h-auto max-h-[600px] object-contain"
                data-testid="ai-artwork-image"
              />
            </div>
            {(hasArtworkTrial || user?.is_premium) && (
              <>
                {/* Style-change callout when user arrived via ?style=X and it differs from current */}
                {searchParams.get("style") &&
                  searchParams.get("style") !== (dream.artwork_style || "surrealist") && (
                    <div
                      className="mt-5 rounded-2xl bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 border-2 border-purple-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                      data-testid="style-change-callout"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-body text-void">
                          {t('dreamDetail.styleChangePrompt', {
                            style: artStyles.find((s) => s.key === selectedStyle)?.label || selectedStyle,
                          }) || `Haluatko luoda uuden version tyylillä ${artStyles.find((s) => s.key === selectedStyle)?.label || selectedStyle}?`}
                        </p>
                      </div>
                      <Button
                        onClick={handleGenerateArtwork}
                        disabled={generatingArtwork}
                        className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-full font-body font-medium px-5 h-10 shadow-md whitespace-nowrap"
                        data-testid="regenerate-with-new-style-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {generatingArtwork
                          ? (t('dreamDetail.generating') || 'Luodaan...')
                          : (t('dreamDetail.createNewVersion') || 'Luo uusi')}
                      </Button>
                    </div>
                  )}

                {/* Style picker — always visible when artwork exists so user can switch */}
                {artStyles.length > 0 && (
                  <div className="mt-5" data-testid="art-style-picker">
                    <p className="text-xs text-void/50 font-body uppercase tracking-wide mb-2">
                      {t('dreamDetail.artStyle') || 'Taidetyyli'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {artStyles.map((s) => (
                        <button
                          key={s.key}
                          disabled={!s.unlocked}
                          onClick={() => s.unlocked && setSelectedStyle(s.key)}
                          className={`px-3 py-1.5 rounded-full text-xs font-body transition-all ${
                            selectedStyle === s.key
                              ? 'bg-void text-white'
                              : s.unlocked
                                ? 'bg-white/60 text-void hover:bg-white/80'
                                : 'bg-void/5 text-void/30 cursor-not-allowed'
                          }`}
                          data-testid={`art-style-${s.key}`}
                        >
                          {s.unlocked ? s.label : `🔒 ${s.label}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleGenerateArtwork}
                  disabled={generatingArtwork}
                  variant="ghost"
                  className="w-full mt-4 text-void/70 hover:text-void hover:bg-white/40 rounded-full font-body text-sm"
                  data-testid="regenerate-artwork-btn"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-2" />
                  {generatingArtwork
                    ? (t('dreamDetail.generating') || 'Luodaan...')
                    : (t('dreamDetail.regenerate') || 'Luo uusi versio')}
                </Button>
              </>
            )}

            {/* Share to community button — visible when artwork exists */}
            {!dream.is_public ? (
              <Button
                onClick={togglePublic}
                className="w-full mt-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-full font-body font-medium h-11 shadow-md"
                data-testid="publish-artwork-to-feed-btn"
              >
                <Globe className="w-4 h-4 mr-2" />
                {t('dreamDetail.publishToFeed') || 'Julkaise unitaide yhteisösyötteeseen'}
              </Button>
            ) : (
              <div
                className="w-full mt-3 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-body text-sm flex items-center justify-center gap-2 h-11"
                data-testid="artwork-published-badge"
              >
                <Globe className="w-4 h-4" />
                <span>{t('dreamDetail.publishedInFeed') || 'Julkaistu yhteisössä'}</span>
                <button
                  onClick={togglePublic}
                  className="text-xs underline ml-2 hover:text-emerald-900"
                  data-testid="unpublish-artwork-btn"
                >
                  {t('dreamDetail.unpublish') || 'Poista'}
                </button>
              </div>
            )}
          </div>
        ) : (user?.is_premium || hasArtworkTrial) && streakCredits === 0 && !streak7Achieved && (
          <div className="glass-effect rounded-3xl p-6 mb-8" data-testid="ai-artwork-empty">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-light text-void mb-0.5">
                    {t('dreamDetail.aiArtwork') || 'AI-Unitaide'}
                  </h3>
                  <p className="text-sm text-void/70 font-body">
                    {user?.is_premium
                      ? (t('dreamDetail.aiArtworkPremiumHint') || 'Luo surrealistinen taideteos unestasi (rajaton)')
                      : (t('dreamDetail.aiArtworkFreeHint') || '1 ilmainen kokeilu jäljellä')}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleGenerateArtwork}
                disabled={generatingArtwork}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-full font-body font-medium px-6 h-11"
                data-testid="generate-artwork-cta"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {generatingArtwork
                  ? (t('dreamDetail.generating') || 'Luodaan...')
                  : (t('dreamDetail.generateArtwork') || 'Luo unitaide')}
              </Button>
            </div>
            {artStyles.length > 0 && (
              <div data-testid="art-style-picker">
                <p className="text-xs text-void/50 font-body uppercase tracking-wide mb-2">
                  {t('dreamDetail.artStyle') || 'Taidetyyli'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {artStyles.map((s) => (
                    <button
                      key={s.key}
                      disabled={!s.unlocked}
                      onClick={() => s.unlocked && setSelectedStyle(s.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-body transition-all ${
                        selectedStyle === s.key
                          ? 'bg-void text-white'
                          : s.unlocked
                            ? 'bg-white/60 text-void hover:bg-white/80'
                            : 'bg-void/5 text-void/30 cursor-not-allowed'
                      }`}
                      data-testid={`art-style-${s.key}`}
                    >
                      {s.unlocked ? s.label : `🔒 ${s.label}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI Analysis Section */}
        <div className="glass-effect rounded-3xl p-8 mb-8">
          <div className="flex flex-col gap-4 mb-6">
            <h2 className="text-2xl font-heading font-light text-void" data-testid="ai-analysis-title">
              AI <span className="italic">{t('dreamDetail.analysis') || 'Analyysi'}</span>
            </h2>
            {!dream.ai_analysis && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl px-4 py-2 font-body text-void w-full sm:w-auto"
                  data-testid="language-select"
                >
                  <option value="english">English</option>
                  <option value="finnish">Suomi</option>
                  <option value="french">Français</option>
                  <option value="german">Deutsch</option>
                  <option value="spanish">Español</option>
                </select>
                <Button
                  onClick={handleAIAnalysis}
                  disabled={analyzing}
                  className="bg-ethereal text-void hover:bg-ethereal/90 rounded-full font-body font-medium transition-colors duration-300 w-full sm:w-auto"
                  data-testid="analyze-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {analyzing ? t('dreamDetail.analyzing') || "Analysoidaan..." : t('dreamDetail.analyzeDream') || "Analysoi uni"}
                </Button>
              </div>
            )}
          </div>

          {dream.ai_analysis ? (
            <>
              <div className="prose prose-lg max-w-none mb-6 overflow-hidden break-words">
                {dream.ai_analysis.split('\n').map((paragraph, idx) => {
                  if (paragraph.trim().startsWith('###')) {
                    const text = paragraph.replace(/###/g, '').trim();
                    return (
                      <div key={idx} className="flex items-center gap-3 my-4">
                        <img src="/logo-new.png" alt="Uneksia" className="w-5 h-5 flex-shrink-0" />
                        <h3 className="text-lg font-heading font-light text-void m-0">{text}</h3>
                      </div>
                    );
                  }
                  else if (paragraph.trim().startsWith('##')) {
                    const text = paragraph.replace(/##/g, '').trim();
                    return (
                      <div key={idx} className="flex items-center gap-3 my-4">
                        <Sparkles className="w-5 h-5 text-lucid flex-shrink-0" />
                        <h2 className="text-xl font-heading font-light text-void m-0">{text}</h2>
                      </div>
                    );
                  }
                  else if (paragraph.trim()) {
                    return (
                      <p key={idx} className="text-void/80 font-body leading-relaxed mb-3">
                        {paragraph}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
              
              {/* Rating Section */}
              <div className="border-t border-white/30 pt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-void/70 font-body">{t('dreamDetail.rateAnalysis') || 'Rate this analysis:'}</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="transition-colors duration-200"
                        data-testid={`star-${star}`}
                      >
                        <Star
                          className={`w-6 h-6 ${
                            star <= (hoverRating || rating)
                              ? 'fill-lucid text-lucid'
                              : 'text-void/30'
                          }`}
                        />
                      </button>
                    ))}
                    {rating > 0 && (
                      <span className="ml-2 text-sm text-void/60 font-body">({rating}/5)</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p className="text-void/60 font-body text-center py-8" data-testid="no-ai-analysis">
              {t('dreamDetail.selectLanguage') || 'Select a language and click "Analyze Dream" to get AI insights'}
            </p>
          )}
        </div>

        {/* Human Analysis Section */}
        <div className="glass-effect rounded-3xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-light text-void" data-testid="human-analysis-title">
              {t('dreamDetail.yourReflections') || 'Your'} <span className="italic">{t('dreamDetail.reflections') || 'Reflections'}</span>
            </h2>
            {!editingHumanAnalysis && (
              <Button
                variant="ghost"
                onClick={() => setEditingHumanAnalysis(true)}
                className="text-void hover:text-void/80 font-body"
                data-testid="edit-analysis-button"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                {dream.human_analysis ? t('common.edit') || "Edit" : t('common.add') || "Add"} {t('dreamDetail.notes') || 'Notes'}
              </Button>
            )}
          </div>

          {editingHumanAnalysis ? (
            <div>
              <Textarea
                value={humanAnalysis}
                onChange={(e) => setHumanAnalysis(e.target.value)}
                placeholder={t('dreamDetail.writeReflections') || "Write your own analysis and reflections..."}
                className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl min-h-[200px] mb-4"
                data-testid="human-analysis-textarea"
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveHumanAnalysis}
                  disabled={savingAnalysis}
                  className="bg-void text-white hover:bg-void/90 rounded-full font-body"
                  data-testid="save-analysis-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingAnalysis ? t('common.saving') || "Saving..." : t('common.save') || "Save"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setHumanAnalysis(dream.human_analysis || "");
                    setEditingHumanAnalysis(false);
                  }}
                  className="bg-white/50 text-void border-void/10 hover:bg-white/80 rounded-full font-body"
                  data-testid="cancel-analysis-button"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : dream.human_analysis ? (
            <div className="prose prose-lg max-w-none">
              <p className="text-void/80 font-body leading-relaxed whitespace-pre-wrap" data-testid="human-analysis-content">
                {dream.human_analysis}
              </p>
            </div>
          ) : (
            <p className="text-void/60 font-body text-center py-8" data-testid="no-human-analysis">
              {t('dreamDetail.addThoughts') || 'Add your own thoughts and reflections about this dream'}
            </p>
          )}
        </div>

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-void/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowShareModal(false)}>
            <div className="glass-effect rounded-3xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-light text-void">{t('dreamDetail.shareYourDream') || 'Share Your Dream'}</h2>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-void/60 hover:text-void transition-colors"
                  data-testid="close-modal-button"
                >
                  ✕
                </button>
              </div>

              {/* Preview */}
              <div className="mb-6 p-4 bg-white/30 rounded-2xl">
                <div className="flex items-center gap-3 mb-3">
                  <img src="/logo-new.png" alt="Uneksia" className="w-6 h-6" />
                  <h3 className="font-heading font-light text-void">{dream.title}</h3>
                </div>
                <p className="text-sm text-void/70 font-body line-clamp-3">
                  {dream.content.substring(0, 150)}...
                </p>
              </div>

              {/* Share Options */}
              <div className="space-y-3">
                <Button
                  onClick={handleShareCard}
                  className="w-full bg-ethereal text-void hover:bg-ethereal/90 rounded-full h-12 font-body font-medium transition-colors duration-300"
                  data-testid="share-card-button"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {t('dreamDetail.shareAsCard') || 'Share as Card Image'}
                </Button>

                <Button
                  onClick={handleDownloadCard}
                  variant="outline"
                  className="w-full bg-white/50 text-void border-void/10 hover:bg-white/80 rounded-full h-12 font-body font-medium"
                  data-testid="download-card-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('dreamDetail.downloadCard') || 'Download Card'}
                </Button>

                {/* AI Artwork - Show button if trial available, premium, or streak reward */}
                {(hasArtworkTrial || user?.is_premium) ? (
                  <Button
                    onClick={handleGenerateArtwork}
                    disabled={generatingArtwork}
                    className={`w-full rounded-full h-12 font-body font-medium transition-all duration-300 ${
                      streakCredits > 0 && !user?.is_premium
                        ? "bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white"
                        : "bg-lucid/20 text-void hover:bg-lucid/30"
                    }`}
                    data-testid="generate-artwork-button"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {generatingArtwork
                      ? t('dreamDetail.generating') || "Generating..."
                      : user?.is_premium
                        ? "✨ Luo AI-taidekuva (Premium)"
                        : streakCredits > 0
                          ? `🎁 Lunasta palkinto: AI-taidekuva (${streakCredits} jäljellä)`
                          : "✨ Luo AI-taidekuva (1 ilmainen)"}
                  </Button>
                ) : (
                  <div className="w-full bg-void/5 text-void/50 rounded-full h-12 font-body font-medium flex items-center justify-center gap-2 border border-dashed border-void/20">
                    <Sparkles className="w-4 h-4" />
                    <span>AI Artwork - {t('premium.upgradeNow') || 'Päivitä Premiumiin'}</span>
                  </div>
                )}

                {!user?.is_premium && !hasArtworkTrial && (
                  <div className="text-center pt-2">
                    <p className="text-xs text-void/60 font-body mb-2">
                      {t('dreamDetail.wantAiArtwork') || 'Haluatko tekoälyn luomia taidekuvia?'}
                    </p>
                    <button
                      onClick={() => navigate('/premium')}
                      className="text-sm text-lucid hover:text-lucid/80 font-body underline"
                    >
                      {t('premium.upgradeNow') || 'Päivitä Premiumiin'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
